import { useCallback } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import WysiwygField from "../Form/Field/WysiwygField";
import Button from "../Buttons/Button";
import withFormStore from "../Form/withFormStore";
import { assignForwardedRef } from "../Utils/assignForwardedRef";
import { useFormatMessage } from "../Intl/Context";
import Icon from "../Utils/Icon";

// Renders a wysiwyg comment form with submit and cancel buttons. Wraps itself
// with `withFormStore` so the form store lifecycle is tied to the form's mount
// state, not to the parent orchestrator.
//
// Accepts a `onSubmit(content)` callback. The `submitLabel`
// prop allows the parent to customize the submit button text depending on the
// context (create vs. edit).
//
const InlineCommentFormInput = ({
  ref,
  comment,
  isEditing = false,
  showCancel = false,
  ...props
}) => {
  const { onSubmit, onCancel, isSubmitting, submitLabel } = props;
  const { validateForm, formState, clearForm } = props;

  const setFormRef = useCallback(
    node => {
      assignForwardedRef(ref, node);
    },
    [ref]
  );

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      // Avoid the contribution's form to receive the submit event, which would trigger
      // unwanted submission (or errors) on the parent contribution form.
      e.stopPropagation();

      if (!validateForm()) return;

      const { value: content } = formState.fields.commentContent;

      onSubmit(content);
    },
    [validateForm, formState.fields.commentContent, onSubmit]
  );

  // Clear the form store when the user cancels so the next open starts fresh.
  const handleCancel = useCallback(() => {
    clearForm();
    onCancel();
  }, [clearForm, onCancel]);

  const t = useFormatMessage();
  const cancelLabel = t(intlMessages.cancel);
  const addCommentLabel = t(intlMessages.addComment);
  const editCommentLabel = t(intlMessages.editComment);
  const hint = isEditing ? editCommentLabel : addCommentLabel;

  const formClassName = classNames("inline-comment-form", { loading: isSubmitting });

  return (
    <form className={formClassName} onSubmit={handleSubmit} ref={setFormRef}>
      <p className="d-flex text-muted margin-bottom-md align-items-center">
        <Icon className="fa fa-info-circle margin-right-xs" />

        {hint}
      </p>

      <WysiwygField
        fieldName="commentContent"
        defaultValue={comment?.content}
        label={false}
        required
        height={200}
      />

      <div className="d-flex margin-top-xs">
        <Button
          type="submit"
          className="btn btn-primary btn-shadow"
          disabled={isSubmitting}
          label={submitLabel}
          iconClassName="fa fa-check"
        />

        {showCancel && (
          <Button
            type="button"
            className="btn btn-default btn-shadow margin-left-xs"
            onClick={handleCancel}
            disabled={isSubmitting}
            label={cancelLabel}
          />
        )}
      </div>
    </form>
  );
};

export default withFormStore(InlineCommentFormInput);

const intlMessages = defineMessages({
  cancel: {
    id: "inlineCommentInput.cancel",
    defaultMessage: "Annuler"
  },
  addComment: {
    id: "inlineCommentForm.addComment",
    defaultMessage: `
      Rédigez votre commentaire ci-dessous puis cliquez sur « Publier » pour le publier.
    `
  },
  editComment: {
    id: "inlineCommentForm.editComment",
    defaultMessage: `
      Modifier votre commentaire ci-dessous puis cliquez sur « Enregistrer » pour
      sauvegarder vos modifications.
    `
  }
});
